'use client';

import type React from 'react';
import Image from 'next/image';

import { urlFor } from '@skybeam/shared-sanity';
import { cn } from '@skybeam/shared-utils/classnames/cn';
import { utils } from '../../../_components/utils';
import type {
  ResponsivePadding,
  ResponsiveValue,
  SanityImage,
} from '../../../_components/utils/types';

type CardItem = {
  icon?: SanityImage;
  text: string;
};

type Card = {
  items: CardItem[];
  subtitle: string;
  title: string;
};

export type TwoColumnCardsSectionProps = {
  backgroundColor?: string;
  cards: Card[];
  containerPadding?: ResponsivePadding;
  contentMaxWidth?: ResponsiveValue;
  heading: string;
};

export const TwoColumnCardsSection = ({
  backgroundColor = 'white',
  cards,
  containerPadding,
  contentMaxWidth,
  heading,
}: TwoColumnCardsSectionProps) => {
  const containerPaddingStyle =
    utils.responsiveUtils.getResponsivePaddingStyleTag(containerPadding);
  const contentMaxWidthStyle = utils.responsiveUtils.getResponsiveValueStyleTag(
    contentMaxWidth,
    'max-width',
  );
  const styleTags = [
    containerPaddingStyle.styleTag,
    contentMaxWidthStyle.styleTag,
  ].filter((tag): tag is React.ReactElement => tag !== null);

  return (
    <>
      {styleTags}
      <div
        className={cn(
          'tablet:py-16 laptop:py-20 desktop:pt-24 desktop:pb-35 flex w-full justify-center px-6 py-12',
          containerPaddingStyle.className,
        )}
        style={{ backgroundColor }}
      >
        <div
          className={cn(
            'desktop:gap-y-20 flex w-full flex-col gap-y-13',
            contentMaxWidthStyle.className,
          )}
        >
          <h2 className="heading-marketing-2 desktop:px-12 text-center font-light">
            {heading}
          </h2>
          <div className="laptop:gap-6 desktop:flex-row desktop:items-center desktop:justify-between flex flex-col gap-4">
            {cards.map((card) => (
              <div
                key={card.title}
                className="tablet:p-8 flex flex-col gap-y-4 rounded-[20px] bg-[#F5F5F580] px-4 py-5"
              >
                <div className="flex flex-col gap-y-2 px-3">
                  <h4 className="heading-marketing-4 tablet:text-3xl desktop:text-[2rem] text-[26px] leading-[0.95] font-light tracking-[-0.64px] text-blue-600">
                    {card.title}
                  </h4>
                  <div className="tablet:text-lg text-base leading-normal text-gray-900">
                    {card.subtitle}
                  </div>
                </div>
                <div>
                  {card.items.map((item, idx) => {
                    const iconUrl =
                      item.icon?.asset &&
                      urlFor(item.icon.asset).width(56).url();
                    return (
                      <div
                        key={item.text}
                        className={cn(
                          'tablet:gap-x-4 tablet:items-center flex gap-x-3 p-3',
                          idx === 1 ? 'rounded bg-white' : '',
                        )}
                      >
                        <div className="tablet:max-h-14 tablet:size-14 tablet:min-w-14 flex max-h-11 min-w-11 items-center justify-center rounded bg-blue-100">
                          {iconUrl ? (
                            <Image
                              src={iconUrl}
                              alt={item.icon?.alt ?? ''}
                              width={24}
                              height={24}
                              className="size-6 object-contain"
                            />
                          ) : null}
                        </div>
                        <div className="laptop:text-lg text-base text-gray-700">
                          {item.text}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};
